import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { maskTimeOfDay, parseTimeOfDay } from "@/lib/occupancy";

import {
  closesLoop,
  datesLabel,
  hasEnded,
  menuLabel,
  predecessorOf,
  shortTime,
  waitsForTurn,
  type Cadence,
  type Menu,
} from "./menu-model";

/**
 * How a menu runs, edited the same way at upload and in its settings (POS2-1456): name, cadence,
 * dates, the menu it comes after, and its service time.
 */

export interface MenuDraft {
  name: string;
  cadence: Cadence;
  /** `YYYY-MM-DD`, empty while unset. */
  startsOn: string;
  endsOn: string;
  predecessorId: string | null;
  /** False = all day. Always-on menus always have a service time. */
  timed: boolean;
  /** `HH:MM`. */
  from: string;
  to: string;
}

/** The `schedule` the API takes on upload and PATCH. */
export interface ScheduleBody {
  cadence: Cadence;
  starts_on: string | null;
  ends_on: string | null;
  daypart_start: string | null;
  daypart_end: string | null;
  predecessor_id: string | null;
}

const DEFAULT_FROM = "11:30";
const DEFAULT_TO = "14:30";

export function draftFromMenu(t: TFunction, locale: Locale, menu: Menu, menus: Menu[]): MenuDraft {
  return {
    name: menuLabel(t, locale, menu),
    cadence: menu.cadence,
    startsOn: menu.starts_on ?? "",
    endsOn: menu.ends_on ?? "",
    predecessorId: predecessorOf(menu, menus)?.id ?? null,
    timed: menu.daypart_start !== null,
    from: menu.daypart_start ? shortTime(menu.daypart_start) : DEFAULT_FROM,
    to: menu.daypart_end ? shortTime(menu.daypart_end) : DEFAULT_TO,
  };
}

function datesOutOfOrder(d: MenuDraft): boolean {
  return d.startsOn !== "" && d.endsOn !== "" && d.endsOn < d.startsOn;
}

/** A whole `HH:MM`: what the masked time field holds once it loses focus. */
function isTime(value: string): boolean {
  return parseTimeOfDay(value) === value;
}

function timesOutOfOrder(d: MenuDraft): boolean {
  return d.timed && isTime(d.from) && isTime(d.to) && d.to <= d.from;
}

export function isValid(d: MenuDraft): boolean {
  if (d.name.trim() === "") return false;
  if (d.timed && (!isTime(d.from) || !isTime(d.to) || timesOutOfOrder(d))) return false;
  if (d.cadence === "always_on") return true;
  return d.startsOn !== "" && d.endsOn !== "" && !datesOutOfOrder(d);
}

export function scheduleBody(d: MenuDraft): ScheduleBody {
  const dated = d.cadence !== "always_on";
  return {
    cadence: d.cadence,
    starts_on: dated ? d.startsOn : null,
    ends_on: dated ? d.endsOn : null,
    daypart_start: d.timed ? d.from : null,
    daypart_end: d.timed ? d.to : null,
    predecessor_id: d.cadence === "cycle" ? d.predecessorId : null,
  };
}

const CADENCES: { key: Cadence; label: string; hint: string }[] = [
  { key: "always_on", label: "menus.cadence.always_on", hint: "menus.fields.alwaysOnHint" },
  { key: "cycle", label: "menus.cadence.cycle", hint: "menus.fields.cycleHint" },
  { key: "one_off", label: "menus.cadence.one_off", hint: "menus.fields.oneOffHint" },
];

const NO_PREDECESSOR = "none";

export function MenuFields({
  draft,
  menus,
  selfId,
  today,
  onChange,
}: {
  draft: MenuDraft;
  /** The site's menus, for "Comes after". */
  menus: Menu[];
  /** The menu being edited, which cannot come after itself; null at upload. */
  selfId: string | null;
  /** The site's today (`useSiteToday`). */
  today: string;
  onChange: (d: MenuDraft) => void;
}) {
  const { t, locale } = useTranslation();
  // A cycle with no end date (from before menus had dates) never ends, so nothing can follow it.
  // An ended one can while it waits for its turn in a rotation (POS2-1469).
  const cycles = menus.filter(
    (m) =>
      m.cadence === "cycle" &&
      m.ends_on !== null &&
      (!hasEnded(m, today) || waitsForTurn(m, menus, today)) &&
      m.id !== selfId,
  );
  const predecessor = menus.find((m) => m.id === draft.predecessorId);
  const rotates = !!predecessor && selfId !== null && closesLoop(predecessor, selfId, menus);
  const set = (patch: Partial<MenuDraft>) => onChange({ ...draft, ...patch });

  return (
    <div className="space-y-5">
      <div>
        <label htmlFor="menu-name" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("menus.fields.name")}
        </label>
        <Input id="menu-name" value={draft.name} onChange={(e) => set({ name: e.target.value })} />
        <p className="mt-1 text-xs text-gray-400">{t("menus.fields.nameHint")}</p>
      </div>

      <div>
        <p className="mb-1.5 text-sm font-medium text-gray-700">{t("menus.fields.howItRuns")}</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup">
          {CADENCES.map((c) => (
            <button
              key={c.key}
              type="button"
              role="radio"
              aria-checked={draft.cadence === c.key}
              onClick={() => set({ cadence: c.key })}
              className={`rounded-md border px-3 py-2 text-left transition-colors ${
                draft.cadence === c.key
                  ? "border-gray-900 bg-gray-50"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <span className="block text-sm font-medium text-gray-900">{t(c.label)}</span>
              <span className="block text-xs text-gray-400">{t(c.hint)}</span>
            </button>
          ))}
        </div>
      </div>

      {draft.cadence !== "always_on" && (
        <div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="menu-starts"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                {t("menus.fields.starts")}
              </label>
              <Input
                id="menu-starts"
                type="date"
                value={draft.startsOn}
                onChange={(e) => set({ startsOn: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="menu-ends" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("menus.fields.ends")}
              </label>
              <Input
                id="menu-ends"
                type="date"
                value={draft.endsOn}
                onChange={(e) => set({ endsOn: e.target.value })}
              />
            </div>
          </div>
          {datesOutOfOrder(draft) && (
            <p className="mt-1 text-xs text-red-600">{t("menus.fields.endsBeforeStart")}</p>
          )}
        </div>
      )}

      {draft.cadence === "cycle" && (
        <div>
          <p className="mb-1.5 text-sm font-medium text-gray-700">{t("menus.fields.comesAfter")}</p>
          <Select
            value={draft.predecessorId ?? NO_PREDECESSOR}
            onValueChange={(v) => set({ predecessorId: v === NO_PREDECESSOR ? null : v })}
          >
            <SelectTrigger className="w-full" aria-label={t("menus.fields.comesAfter")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PREDECESSOR}>{t("menus.fields.comesAfterNothing")}</SelectItem>
              {cycles.map((m) => {
                const dates = datesLabel(locale, m);
                return (
                  <SelectItem key={m.id} value={m.id}>
                    {dates ? `${menuLabel(t, locale, m)} (${dates})` : menuLabel(t, locale, m)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-gray-400">
            {draft.predecessorId === null
              ? t("menus.fields.comesAfterNothingHint")
              : rotates
                ? t("menus.fields.rotatesHint")
                : t("menus.fields.comesAfterHint")}
          </p>
        </div>
      )}

      {draft.timed ? (
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">{t("menus.fields.serviceTime")}</p>
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-700"
              onClick={() => set({ timed: false })}
            >
              {t("menus.fields.allDayInstead")}
            </button>
          </div>
          {/* Typed 24-hour, not <input type="time">, which follows the browser's region and
              can be 12-hour: see parseTimeOfDay. Selected on focus so typing replaces it. */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              inputMode="numeric"
              maxLength={5}
              placeholder={t("menus.fields.timePlaceholder")}
              aria-label={t("menus.fields.serviceFrom")}
              value={draft.from}
              onFocus={(e) => e.target.select()}
              onMouseUp={(e) => e.preventDefault()}
              onChange={(e) => set({ from: maskTimeOfDay(e.target.value) })}
              onBlur={(e) => set({ from: parseTimeOfDay(e.target.value) ?? "" })}
            />
            <Input
              inputMode="numeric"
              maxLength={5}
              placeholder={t("menus.fields.timePlaceholder")}
              aria-label={t("menus.fields.serviceTo")}
              value={draft.to}
              onFocus={(e) => e.target.select()}
              onMouseUp={(e) => e.preventDefault()}
              onChange={(e) => set({ to: maskTimeOfDay(e.target.value) })}
              onBlur={(e) => set({ to: parseTimeOfDay(e.target.value) ?? "" })}
            />
          </div>
          <p
            className={`mt-1 text-xs ${timesOutOfOrder(draft) ? "text-red-600" : "text-gray-400"}`}
          >
            {timesOutOfOrder(draft)
              ? t("menus.fields.serviceOutOfOrder")
              : t("menus.fields.serviceTimeHint")}
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">{t("menus.fields.serviceTime")}</p>
            <p className="text-xs text-gray-400">{t("menus.fields.allDayHint")}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-600"
            onClick={() => set({ timed: true })}
          >
            {t("menus.fields.setTimes")}
          </Button>
        </div>
      )}
    </div>
  );
}
